import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import axios from 'axios';

const ServiceSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bilId, setBilId] = useState('');
  const [interntNummer, setInterntNummer] = useState('');

  useEffect(() => {
    const bil_id = searchParams.get('bil_id');
    const internt_nummer = searchParams.get('internt_nummer');

    setBilId(bil_id);
    setInterntNummer(internt_nummer);

    fetchCategories(bil_id);
  }, [searchParams]);

  useEffect(() => {
    const categoriesParam = searchParams.get('categories');
    if (!categoriesParam) return;

    const parsed = categoriesParam
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id));

    if (parsed.length) {
      setSelectedCategories(parsed);
    }
  }, [searchParams]);

  const fetchCategories = async (bil_id) => {
    try {
      const response = await axios.get('/api/services/categories', {
        params: { bil_id }
      });
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedCategories.length === 0) {
      alert('Vælg mindst én kategori');
      return;
    }

    const resumeFrom = searchParams.get('resume_from');
    // Naviger til service details med valgte kategorier
    const categoryIds = selectedCategories.join(',');
    const resumeParam = resumeFrom ? `&resume_from=${encodeURIComponent(resumeFrom)}` : '';
    navigate(`/services/details?bil_id=${bilId}&internt_nummer=${interntNummer}&categories=${categoryIds}${resumeParam}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/biler/profil/${bilId}`)}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vælg Service Kategorier</h1>
          <p className="text-gray-600">Køretøj: <strong>{interntNummer}</strong></p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Vælg kategorier:</h2>
            {categories.length === 0 ? (
              <p className="text-gray-600">Ingen kategorier tilgængelige for denne vogntype.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedCategories.includes(category.id)
                        ? 'border-falck-blue bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="selected_categories"
                      value={category.id}
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => handleCategoryChange(category.id)}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium text-gray-900">{category.name}</span>
                      {selectedCategories.includes(category.id) && (
                        <FaCheck className="text-falck-blue text-lg" />
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/biler/profil/${bilId}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Tilbage
            </button>
            <button
              type="submit"
              disabled={selectedCategories.length === 0}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                selectedCategories.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-falck-blue hover:bg-blue-700'
              }`}
            >
              Fortsæt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceSelection;
